/**
 * „Wyloguj się" on the Profil tab's last group (owner, 4 November 2026; it stood in the page
 * header until then), and the confirm it asks first — there is no undo. A secondary pill: leaving
 * is not the tab's primary act and not a destructive one.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { routeForSessionStatus, signOut } from '~/entities/session';
import { leaveDocument } from '~/shared/lib/documentExit';
import { ConfirmDialog, type ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import { SECONDARY_PILL } from './accountButtons';
import { SignOutIcon } from './AccountIcons';
import styles from './AccountScreen.module.css';

export function SignOutButton() {
  const { t } = useTranslation();
  const [signOutPrompt, setSignOutPrompt] = useState<ConfirmPrompt | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  return (
    <>
      <button
        type="button"
        className={SECONDARY_PILL}
        onClick={() => {
          setSignOutPrompt({
            title: t('web:account.signOut.confirmTitle'),
            description: t('web:account.signOut.confirmBody'),
            confirmLabel: t('web:account.signOut.action'),
            cancelLabel: t('common.cancel'),
          });
        }}
      >
        <SignOutIcon className={styles.pillGlyph} />
        {t('web:account.signOut.action')}
      </button>

      <ConfirmDialog
        prompt={signOutPrompt === null ? null : { ...signOutPrompt, busy: signingOut }}
        onCancel={() => {
          setSignOutPrompt(null);
        }}
        onConfirm={() => {
          setSigningOut(true);
          // The session's own routing table decides where a signed-out trainer belongs, so the
          // destination is not restated. `signOut` cancels and clears the query cache first.
          //
          // A local sign-out that fails still has to land on the sign-in route: a busy button that
          // never resolves is worse than a session the route's own gate will re-check on arrival.
          void signOut()
            .catch(() => undefined)
            .finally(() => {
              leaveDocument(routeForSessionStatus('signedOut') ?? '/sign-in');
            });
        }}
      />
    </>
  );
}
